import { getPlanPricing } from '@/api/next-apis/plan-pricing-api';
import { objectAtomFamily, plansCheckoutReturnUrlAtom, plansModalOpenAtom } from '@/jotai/atom';
import { atomKeys } from '@/jotai/atom-keys';
import { Redirects } from '@/utils/config';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AI_CREDITS_QUERY_KEY } from '@/hooks/billing/use-ai-credits';
import { useExclusiveOffer } from '@/hooks/common/use-exclusive-offer';
import { getActiveOrgId } from '@/lib/active-org';
import { startPlanCheckout } from '@/lib/plan-checkout';
import {
  ANNUAL_BILLING_SAVINGS_PERCENT,
  dodoCurrencies,
  localizePlans,
  normalizePlanName,
  type UpgradePlanName,
} from '@workspace/global-utils';
import { PlansModal } from '@workspace/ui/components/plans-modal';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';

const DEFAULT_CURRENCY = 'USD';

// Same plans, prices and currency list as the dashboard billing page.
const PlansUpgradeModal = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useAtom(plansModalOpenAtom);
  const [returnUrl, setReturnUrl] = useAtom(plansCheckoutReturnUrlAtom);
  const projectInfo = useAtomValue(objectAtomFamily(atomKeys.projectInfo)) as ProjectInfo;
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  // Checkout always leaves the page (Dodo, or billing as the fallback), so this never resets.
  const [pendingPlan, setPendingPlan] = useState<UpgradePlanName | null>(null);
  const { offer, talkToFounder } = useExclusiveOffer();

  const { data: localized, isFetching } = useQuery({
    queryKey: ['plan-pricing', currency],
    queryFn: () => getPlanPricing(currency),
    enabled: open && currency !== DEFAULT_CURRENCY,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const plans = useMemo(() => localizePlans(localized), [localized]);

  // Outside a document (the agent page) there is no project info; the credits query knows the plan.
  const currentPlan = normalizePlanName(
    projectInfo?.plan_name ??
      queryClient.getQueryData<{ planName: string }>(AI_CREDITS_QUERY_KEY)?.planName,
  );

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setReturnUrl(null);
  };

  const openBilling = () => {
    handleOpenChange(false);
    const orgId = projectInfo?.org_id ?? getActiveOrgId();
    if (orgId == null) return;
    window.open(Redirects.DASHBOARD.BILLING(orgId), '_blank', 'noopener');
  };

  return (
    <PlansModal
      open={open}
      onOpenChange={handleOpenChange}
      plans={plans}
      pricesLoading={isFetching}
      currentPlan={currentPlan === 'free' || currentPlan === 'Enterprise' ? undefined : currentPlan}
      currentPlanLabel={currentPlan === 'free' ? 'Free' : currentPlan}
      annualSavingsPercent={ANNUAL_BILLING_SAVINGS_PERCENT}
      currency={currency}
      currencies={dodoCurrencies}
      onCurrencyChange={setCurrency}
      offer={offer}
      onTalkToFounder={talkToFounder}
      onSelectPlan={(plan, interval) => {
        setPendingPlan(plan);
        void startPlanCheckout(plan, interval, currency, returnUrl ?? undefined);
      }}
      pendingPlan={pendingPlan}
      onCompareFeatures={() => openBilling()}
      dataPhSelector="builder-plans-modal"
    />
  );
};

export default PlansUpgradeModal;
